import { useRef, useCallback, useEffect, useState } from "react";
import { Logger } from "@cede/utils";
import { THROTTLE_DELAY, MAX_RECONNECT_ATTEMPTS, MAX_RECONNECT_DELAY } from "../constants";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";

const EXCHANGE_ID = "coinbase";
const WS_URL = "wss://ws-feed.exchange.coinbase.com";

/**
 * Format pair symbol for Coinbase WebSocket
 * BTC/USDT → BTC-USDT
 */
function formatSymbol(pairSymbol: string): string {
  return pairSymbol.replace("/", "-");
}

// Orderbook state - maintained at module level to persist across re-renders
const orderbookState: {
  bids: Map<string, string>;
  asks: Map<string, string>;
  currentSymbol: string | null;
} = {
  bids: new Map(),
  asks: new Map(),
  currentSymbol: null,
};

/**
 * Hook for connecting to Coinbase WebSocket and subscribing to orderbook updates
 * Coinbase uses snapshot + l2update delta pattern
 */
export const useCoinbaseOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const isConnecting = useRef(false);
  const reconnectAttemptsRef = useRef<number>(0);

  const [isConnected, setIsConnected] = useState(false);
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateOrderBookData = useCallback((data: DirectOrderBookSnapshot) => {
    setOrderBookData(data);
    setError(null);
  }, []);

  const handleOrderBookUpdate = useCallback(
    (data: DirectOrderBookSnapshot) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        lastUpdateTimeRef.current = now;
        updateOrderBookData(data);
      } else {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }

        const remainingTime = THROTTLE_DELAY - timeSinceLastUpdate;
        throttleTimeoutRef.current = setTimeout(() => {
          lastUpdateTimeRef.current = Date.now();
          updateOrderBookData(data);
        }, remainingTime);
      }
    },
    [updateOrderBookData],
  );

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    isConnecting.current = false;
    setIsConnected(false);
    lastUpdateTimeRef.current = 0;
  }, []);

  const subscribeToOrderBook = useCallback((ws: WebSocket, symbol: string) => {
    const subscribeMessage = {
      type: "subscribe",
      product_ids: [symbol],
      channels: ["level2_batch"],
    };

    ws.send(JSON.stringify(subscribeMessage));
  }, []);

  /**
   * Convert module-level maps to sorted arrays and create snapshot
   */
  const createSnapshotFromState = useCallback((symbol: string): DirectOrderBookSnapshot => {
    // Convert bids map to sorted array (highest price first), limit to top 20
    const bids: [number, number][] = Array.from(orderbookState.bids.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => b[0] - a[0])
      .slice(0, 20);

    // Convert asks map to sorted array (lowest price first), limit to top 20
    const asks: [number, number][] = Array.from(orderbookState.asks.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => a[0] - b[0])
      .slice(0, 20);

    return {
      timestamp: Date.now(),
      exchangeId: EXCHANGE_ID,
      pairSymbol: symbol,
      bids,
      asks,
    };
  }, []);

  const connectWs = useCallback(() => {
    if (isConnecting.current || !enabled || !pairSymbol) return;

    disconnectWs();

    const symbol = formatSymbol(pairSymbol);

    // Reset orderbook state only if symbol changed
    if (orderbookState.currentSymbol !== pairSymbol) {
      orderbookState.bids = new Map();
      orderbookState.asks = new Map();
      orderbookState.currentSymbol = pairSymbol;
    }

    isConnecting.current = true;
    setError(null);
    setOrderBookData(null);

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        setIsConnected(true);
        isConnecting.current = false;
        reconnectAttemptsRef.current = 0;

        // Subscribe to orderbook
        subscribeToOrderBook(ws, symbol);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          // Handle snapshot - full orderbook reset
          if (msg.type === "snapshot") {
            orderbookState.bids = new Map();
            orderbookState.asks = new Map();

            // Load bids from snapshot
            if (msg.bids) {
              for (const [price, size] of msg.bids) {
                orderbookState.bids.set(price, size);
              }
            }

            // Load asks from snapshot
            if (msg.asks) {
              for (const [price, size] of msg.asks) {
                orderbookState.asks.set(price, size);
              }
            }

            // Create snapshot from updated state
            const snapshot = createSnapshotFromState(pairSymbol);
            handleOrderBookUpdate(snapshot);
          }
          // Handle l2update - delta updates
          else if (msg.type === "l2update") {
            // changes format: [side, price, size]
            if (msg.changes) {
              for (const [side, price, size] of msg.changes) {
                const map = side === "buy" ? orderbookState.bids : orderbookState.asks;
                if (parseFloat(size) === 0) {
                  map.delete(price);
                } else {
                  map.set(price, size);
                }
              }
            }

            // Create snapshot from updated state
            const snapshot = createSnapshotFromState(pairSymbol);
            handleOrderBookUpdate(snapshot);
          }
          // Ignore subscription acks and other messages
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to parse orderbook update:`, errorMessage, err);
          setError("Failed to parse orderbook update");
        }
      };

      ws.onerror = () => {
        isConnecting.current = false;
        setIsConnected(false);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        isConnecting.current = false;
        setIsConnected(false);

        if (enabled && pairSymbol && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY);
          reconnectAttemptsRef.current++;

          Logger.log(
            `[${EXCHANGE_ID}OrderBookWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
            Logger.types.INFO,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWs();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Max reconnection attempts reached, giving up`);
          setError("Failed to reconnect after multiple attempts");
        }
      };

      wsRef.current = ws;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to create WebSocket connection:`, errorMessage, err);
      isConnecting.current = false;
      setError("Failed to create WebSocket connection");
    }
  }, [pairSymbol, enabled, disconnectWs, handleOrderBookUpdate, subscribeToOrderBook, createSnapshotFromState]);

  useEffect(() => {
    if (!enabled || !pairSymbol) {
      disconnectWs();
      return;
    }

    connectWs();

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectWs();
      } else if (enabled && pairSymbol) {
        connectWs();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
      disconnectWs();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [pairSymbol, enabled, connectWs, disconnectWs]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error,
  };
};
